import { MouseEvent, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Check, Copy, X } from '@digital-twins/design-system/icons'
import { KnowledgeAskCitation, KnowledgeDocumentListItem } from '@digital-twins/shared-types'

import { reconcileArtifactBody } from '@/components/artifact-editor/reconcileArtifactBody'
import {
  StudyDocPlotLookup,
  StudyDocPrintButton,
  StudyDocQuote,
  STUDY_DOC_READING_COLUMN_CLASS,
} from '@/components/study-doc'
import { StudyDocDecisionConfidence } from '@/components/study-doc/StudyDocDecisionMasthead/StudyDocDecisionMasthead'
import { STUDY_DOC_CARD_PAD, studyDocCardPadStyle } from '@/components/study-doc/StudyDocMagazineBand/StudyDocMagazineBand'
import { StudyDocStripLookup } from '@/components/study-doc/StudyDocMagazineBand/resolveMagazineBandSource'
import { Text } from '@/components/ui/Text'
import { ThreadAssetView } from '@/lib/api'

import { artifactDisplayLabel, bodyOpensWithHeading, localizeArtifactCategory } from './artifactDisplayLabel'
import { ThreadArtifactItem } from './collectThreadArtifacts'
import { ThreadArtifactBodyPreview } from './ThreadArtifactBodyPreview'
import { useThreadArtifactTips } from './useThreadArtifactTips'

/**
 * Full-viewport StudyDoc / artifact reading surface (ChatGPT Expand).
 *
 * Distinct from the rail's `ThreadArtifactExpandedView`: that one stays in the
 * pane; this one covers the thread. Escape or the X closes it.
 *
 * Read-only. Editing lives on the collapsed card in the thread column. The
 * top bar names the artifact's category only ("Report", "Desk research"):
 * the body carries its own title.
 */

export interface ThreadArtifactFullscreenViewProps {
  item: ThreadArtifactItem
  onClose: () => void
  plots?: Record<string, StudyDocPlotLookup>
  quotes?: Record<string, StudyDocQuote>
  plotRegion?: 'all' | 'plot'
  /** Slim picker population for mosaic tiles missing a row image. */
  populationId?: string
  /** FactBundle strip lookup for `{{quotes_strip:…}}` full-bleed. */
  strips?: Record<string, StudyDocStripLookup>
  /** P(best) masthead. Absent → empty-stable, no masthead. */
  decisionConfidence?: StudyDocDecisionConfidence | null
  threadAssets?: Array<ThreadAssetView>
  docsByToken?: Map<string, KnowledgeDocumentListItem>
  onOpenCitation?: (index: number, citation: KnowledgeAskCitation) => void
}

export function ThreadArtifactFullscreenView(
  props: ThreadArtifactFullscreenViewProps,
) {
  const {
    item,
    onClose,
    plots,
    quotes,
    plotRegion,
    populationId,
    strips,
    decisionConfidence,
    threadAssets,
    docsByToken,
    onOpenCitation,
  } = props

  const { t } = useTranslation('app')

  const [hasCopied, setHasCopied] = useState(false)
  const { tipForItem } = useThreadArtifactTips(item.runToken)
  const tip = tipForItem(item)
  const displayBody = reconcileArtifactBody(item.body, tip)
  const label = artifactDisplayLabel({ ...item, body: displayBody })
  const categoryText = localizeArtifactCategory(label.category, label.letter)
  const accessibleName = categoryText
    ? `${categoryText}: ${label.headline}`
    : label.headline
  const printTitle = item.title || label.headline || accessibleName
  const showSynthesizedTitle =
    !bodyOpensWithHeading(displayBody) && label.category === null

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  async function handleCopyClick(event: MouseEvent<HTMLButtonElement>) {
    event.stopPropagation()
    if (!navigator.clipboard?.writeText) {
      return
    }
    try {
      await navigator.clipboard.writeText(displayBody)
      setHasCopied(true)
      window.setTimeout(() => setHasCopied(false), 1500)
    } catch {
      setHasCopied(false)
    }
  }

  // Thread scroll regions use masks and containment; the viewport reader must escape them.
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex flex-col bg-background"
      data-testid="thread-artifact-fullscreen"
      role="dialog"
      aria-modal="true"
      aria-label={accessibleName}
    >
      <div className="flex items-center gap-2 border-b border-border px-4 py-3">
        <Text
          as="span"
          size="sm"
          className="min-w-0 flex-1 truncate"
          data-testid="thread-artifact-fullscreen-title"
        >
          <Text as="span" size="sm" weight="semibold">
            {categoryText ?? label.headline}
          </Text>
        </Text>
        <button
          type="button"
          className="shrink-0 rounded p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
          data-testid="thread-artifact-fullscreen-copy"
          aria-label={
            hasCopied
              ? t('threads.detail.rail.copiedNamedAria', { name: accessibleName })
              : t('threads.detail.rail.copyNamedAria', { name: accessibleName })
          }
          onClick={handleCopyClick}
        >
          {hasCopied ? (
            <Check className="h-3.5 w-3.5" aria-hidden />
          ) : (
            <Copy className="h-3.5 w-3.5" aria-hidden />
          )}
        </button>
        <StudyDocPrintButton
          accessibleName={accessibleName}
        />
        <button
          type="button"
          className="shrink-0 rounded p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
          data-testid="thread-artifact-fullscreen-close"
          aria-label={t('threads.detail.rail.closeFullscreenAria')}
          onClick={onClose}
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>

      <div
        className="min-h-0 flex-1 overflow-y-auto px-6 py-6"
        style={studyDocCardPadStyle(STUDY_DOC_CARD_PAD.fullscreen, 'viewport')}
        data-study-doc-print-root=""
        data-study-doc-print-title={printTitle}
      >
        <div
          className={STUDY_DOC_READING_COLUMN_CLASS}
          data-testid="thread-artifact-fullscreen-column"
        >
          {showSynthesizedTitle ? (
            <h1
              className="mb-2 mt-0 font-display text-2xl font-medium leading-tight text-foreground"
              data-testid="thread-artifact-fullscreen-heading"
            >
              {label.headline}
            </h1>
          ) : null}
          <ThreadArtifactBodyPreview
            body={displayBody}
            plots={plots}
            quotes={quotes}
            plotRegion={plotRegion}
            populationId={populationId}
            strips={strips}
            decisionConfidence={decisionConfidence}
            threadAssets={threadAssets}
            docsByToken={docsByToken}
            onOpenCitation={onOpenCitation}
            runToken={item.runToken}
          />
        </div>
      </div>
    </div>,
    document.body,
  )
}
